import { useEffect, useState } from "react";
import { Loader2, Flame, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fetchToday, generateToday, fetchPublicToday, reviewTerm, fetchMyStats, formatApiError } from "@/lib/inmindApi";
import { useAuth } from "@/lib/AuthContext";
import ThinkPanel from "@/components/ThinkPanel";

// Date header helpers
const WEEKDAYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
function todayLabel(streakDay) {
  const d = new Date();
  return `${WEEKDAYS[d.getDay()]}${streakDay ? ` · DAY ${streakDay}` : ""}`;
}

export default function HomePage() {
  const { user } = useAuth();
  const isGuest = !user;
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [thinkOpen, setThinkOpen] = useState(false);
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const c = isGuest ? await fetchPublicToday() : await fetchToday();
        setCard(c);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load today's card");
      } finally {
        setLoading(false);
      }
      if (!isGuest) {
        try {
          const s = await fetchMyStats();
          setStreak(s?.streak ?? s?.current_streak ?? null);
        } catch { /* stats optional */ }
      }
    })();
  }, [isGuest]);

  const draw = async () => {
    if (isGuest) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const c = await generateToday();
      setCard(c);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draw a card");
    } finally {
      setBusy(false);
    }
  };

  const toggleSaved = async () => {
    if (!card) return;
    if (isGuest) {
      navigate("/login");
      return;
    }
    const next = card.status === "reviewed" ? "new" : "reviewed";
    setBusy(true);
    try {
      const updated = await reviewTerm(card.id, next);
      setCard(updated);
      toast.success(next === "reviewed" ? "Saved" : "Removed from saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const openThink = () => {
    if (isGuest) {
      navigate("/login");
      return;
    }
    setThinkOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" style={{ color: "var(--ink-muted)" }}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading today…
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pt-8 pb-16 px-1" data-testid="today-page">
      {/* Top row: date + streak */}
      <div className="flex items-center justify-between px-1 mb-8">
        <div
          className="text-[10px] font-semibold"
          style={{
            letterSpacing: "0.18em",
            color: "var(--ink-muted)",
          }}
          data-testid="today-date-label"
        >
          {todayLabel(streak)}
        </div>
        {streak != null && (
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
            style={{ background: "var(--ink-elevated)", color: "var(--ink-secondary)", fontSize: 11 }}
            data-testid="today-streak-pill"
          >
            <Flame className="w-3.5 h-3.5" strokeWidth={1.8} />
            <span className="font-medium">{streak}</span>
          </div>
        )}
      </div>

      {!card ? (
        <EmptyState onDraw={draw} busy={busy} />
      ) : (
        <div
          className="rounded-3xl p-7 sm:p-9"
          style={{
            background: "#F7F4EC",
            boxShadow: "0 24px 60px rgba(0,0,0,0.35), 0 4px 14px rgba(0,0,0,0.18)",
          }}
          data-testid="today-card"
        >
          {/* ELEMENT 1 — Category tag: small outlined pill, LEFT-aligned, top of card */}
          {card.category && (
            <div className="mb-7">
              <span
                className="inline-flex items-center rounded-full px-3 py-1.5"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "#5A5972",
                  border: "1px solid #D9D3C4",
                  background: "transparent",
                  fontWeight: 600,
                }}
                data-testid="card-category"
              >
                {(card.category || "").replace(/_/g, " ")}
              </span>
            </div>
          )}

          {/* ELEMENT 2 — Scene: italic, second person, sets the feeling */}
          {card.scene && (
            <p
              className="italic"
              style={{
                fontSize: 17,
                lineHeight: 1.65,
                color: "#3A3A55",
                fontWeight: 400,
              }}
              data-testid="card-scene"
            >
              &ldquo;{card.scene}&rdquo;
            </p>
          )}

          {/* ELEMENT 3 — Thin hairline divider, full width */}
          {card.scene && (
            <div className="mt-8 mb-8" style={{ height: 1, background: "#E0DAC9" }} />
          )}

          {/* ELEMENT 4 — Concept word: large serif, the "aha" moment */}
          <h1
            className="font-serif"
            style={{
              fontSize: 40,
              fontWeight: 400,
              color: "#141428",
              lineHeight: 1.12,
              letterSpacing: "-0.01em",
              fontFamily: "Georgia, 'Times New Roman', serif",
            }}
            data-testid="card-term"
          >
            {card.term}
          </h1>

          {/* ELEMENT 5 — Phonetic pronunciation */}
          {card.pronunciation && (
            <div
              className="mt-2"
              style={{ fontSize: 13, color: "#7A7A93", letterSpacing: "0.02em" }}
              data-testid="card-pronunciation"
            >
              /&thinsp;{card.pronunciation}&thinsp;/
            </div>
          )}

          {/* ELEMENT 6 — Definition: plain, warm, human */}
          <p
            className="mt-6"
            style={{
              fontSize: 16,
              lineHeight: 1.7,
              color: "#2E2E45",
              fontWeight: 400,
            }}
            data-testid="card-meaning"
          >
            {card.meaning}
          </p>

          {/* ELEMENT 7 — Reflection question: distinct container with left border accent */}
          {(card.reflection_question || card.fact) && (
            <div
              className="mt-8 rounded-md pl-5 pr-4 py-4"
              style={{
                background: "#EDE7D6",
                borderLeft: "3px solid #2D2B55",
              }}
              data-testid="card-reflection"
            >
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: "#3A3A55",
                  fontStyle: "normal",
                }}
              >
                {card.reflection_question || card.fact}
              </p>
            </div>
          )}

          {/* Bottom action row — Save · Ask Think */}
          <div className="mt-9 flex items-center gap-3">
            <button
              onClick={toggleSaved}
              disabled={busy}
              data-testid="card-save-btn"
              className="flex-1 rounded-full h-12 text-sm font-medium transition-colors"
              style={{
                background: "transparent",
                color: "#2D2B55",
                border: "1px solid #C9C2AF",
              }}
            >
              {card.status === "reviewed" ? "Saved" : "Save"}
            </button>

            <button
              onClick={openThink}
              data-testid="open-think-btn"
              className="flex-1 rounded-full h-12 text-sm font-semibold transition-colors"
              style={{
                background: "#141428",
                color: "#F7F4EC",
                border: "1px solid #141428",
                letterSpacing: "0.01em",
              }}
            >
              Ask Think
            </button>
          </div>
        </div>
      )}

      {card && isGuest && (
        <p
          className="mt-6 text-center"
          style={{ fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.6 }}
          data-testid="guest-hint"
        >
          <Link to="/login" className="underline underline-offset-2" style={{ color: "var(--ink-heading)" }}>
            Sign in
          </Link>{" "}
          to save this word and think it through with your daily AI partner.
        </p>
      )}

      {card && (
        <Sheet open={thinkOpen} onOpenChange={setThinkOpen}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-xl p-0 flex flex-col border-none shadow-none"
            style={{ background: "var(--ink-canvas)" }}
            data-testid="home-think-sheet"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Ask Think about {card.term}</SheetTitle>
              <SheetDescription>A curious, Socratic thinking partner.</SheetDescription>
            </SheetHeader>
            {/* Concept chip at the top of Think */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: "var(--ink-border)" }}
            >
              <div
                className="font-serif"
                style={{ fontSize: 16, color: "var(--ink-heading)" }}
                data-testid="think-context-chip"
              >
                {card.term}
              </div>
              <div
                className="text-[9px] font-semibold"
                style={{ letterSpacing: "0.16em", color: "var(--ink-muted)", textTransform: "uppercase" }}
              >
                Ask Think
              </div>
            </div>
            <div className="flex-1 p-3 sm:p-4 overflow-hidden">
              <ThinkPanel key={card.id} termId={card.id} termName={card.term} />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

function EmptyState({ onDraw, busy }) {
  return (
    <div
      className="rounded-3xl p-8 text-center"
      style={{
        background: "var(--ink-canvas)",
        border: "1px solid var(--ink-border)",
      }}
      data-testid="today-empty"
    >
      <p
        className="font-serif"
        style={{ fontSize: 22, color: "var(--ink-heading)", fontWeight: 400 }}
      >
        One concept · one minute
      </p>
      <p className="mt-2" style={{ fontSize: 13, color: "var(--ink-body)", lineHeight: 1.6 }}>
        Pull today&apos;s idea, then sit with it.
      </p>
      <button
        onClick={onDraw}
        disabled={busy}
        data-testid="generate-today"
        className="mt-6 inline-flex items-center gap-2 rounded-full h-11 px-6 text-sm font-medium"
        style={{ background: "var(--ink-elevated)", color: "var(--ink-light)" }}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.8} />}
        Draw today&apos;s card
      </button>
    </div>
  );
}
